<script setup lang="ts">
import type { MenuLink } from '@/types/common'

import FooterLinksDownloadButton from '@/components/Footer/FooterLinksDownloadButton.vue'
import FooterSocialNetworks from '@/components/Footer/FooterSocialNetworks.vue'

type Props = {
  menuLinks: MenuLink[]
}

interface ImportantLinks {
  title: string
  anchor: string
}

const props = defineProps<Props>()

const route = useRoute()

const importantLinks = [
  {
    title: 'Новости',
    anchor: 'newsBlock',
  },
  {
    title: 'Цифры дня',
    anchor: 'dailyStatistics',
  },
  {
    title: 'Фото',
    anchor: 'photoBlock',
  },
] as ImportantLinks[]

const sidebarMenuLinks = computed(() => {
  const menuList = [...props.menuLinks]

  menuList.push({
    title: 'Политика конфиденциальности',
    url: '/confidential/',
  })

  menuList.push({
    title: 'Пользовательское соглашение',
    url: '/terms_of_use/',
  })

  return menuList
})

const onClickAnchorLink = (link: ImportantLinks) => {
  const buttonCloseElement = document.querySelector<HTMLElement>(
    '#btnCloseCommonSidebar',
  )

  const scrollToAnchor = (timeout: number) => {
    setTimeout(() => {
      document
        .querySelector<HTMLElement>(`#${link.anchor}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, timeout)
  }

  if (buttonCloseElement) {
    buttonCloseElement?.click()
  }

  //  Если находимся не на главной
  //  Сначала редиректим, потом в common page скроллим
  if (route.path !== '/') {
    navigateTo('/')
    scrollToAnchor(1000)
    return
  }

  // Находимся на разводящей - скролим сразу
  scrollToAnchor(200)
}
</script>

<template>
  <div class="sidebar" data-test-id="sidebar">
    <div class="sidebar__important-links" data-test-id="important-links">
      <div
        v-for="link in importantLinks"
        :key="link.anchor"
        class="sidebar__important-link"
        data-test-id="important-link"
        @click="onClickAnchorLink(link)"
      >
        {{ link.title }}
      </div>
    </div>

    <div class="sidebar__links">
      <a
        v-for="link in sidebarMenuLinks"
        :key="link.url"
        class="sidebar__link"
        :href="link.url"
        data-test-id="menu-link"
        target="_blank"
      >
        {{ link.title }}
      </a>
    </div>

    <footer-links-download-button
      class="sidebar__download-button"
      data-test-id="download-button"
    />

    <footer-social-networks
      width="33"
      height="33"
      data-test-id="social-networks"
      class="sidebar__social-networks"
    />

    <div class="sidebar__social-sign" data-test-id="copyright">
      СДЕЛАНО В
      <a
        class="sidebar__smena-link"
        href="https://smena.space/"
        data-test-id="smena-link"
      >
        SMENA
      </a>
    </div>
  </div>
</template>

<style scoped lang="scss">
.sidebar {
  width: 100%;
  height: 100%;
  padding: 35px 20px 52px 20px;
  background-color: $background-dark;

  &__important-link {
    font-size: 24px;
    font-weight: 800;
    line-height: 50px;
    color: var(--ui-color-white);
  }

  &__links {
    display: flex;
    flex-direction: column;
    margin-top: 10px;
  }

  &__link {
    display: flex;
    align-items: center;
    height: 40px;
    font-size: 18px;
    font-weight: 400;
    color: var(--ui-color-white);
  }

  &__download-button {
    margin-top: 30px;
  }

  &__social-networks {
    gap: 35px;
    margin-top: 33px;
  }

  &__social-sign {
    margin-top: 33px;
    font-size: 10px;
    line-height: 12px;
    color: var(--ui-color-white);
  }

  &__smena-link {
    color: var(--ui-color-white);
    text-decoration: underline;
  }

  @include media($bp-tablet) {
    padding-top: 156px; // 156px = 131 - высота хедера + 35 отступ сверху
  }
}
</style>
